import { FormProvider, useForm } from 'react-hook-form';
import React, { FunctionComponent, useContext } from 'react';

import { AllFormData } from '../../../types/givingCheckoutForm';
import { AuthContext } from '@justgiving/auth-react';
import { createGraphQlClient } from '../../graphQl/baseApi';
import { useCharityData } from '../../context/charityContext';
import { useDonationLinkQuery } from '../../../types/__generated-graphQL__';
import { useQueryParameters } from '../../hooks/useQueryParameters';

export const suggestedAmountDefaults = {
  suggestedAmounts: {
    amount1: '10',
    amount2: '20',
    amount3: '30',
    amount4: '50',
    amount5: '100',
  },
  numberOfCustomAmounts: '5',
  suggestedAmountDefault: '3',
};

type FormContextProps = {
  children: React.ReactNode;
};

export const FormContext: FunctionComponent<FormContextProps> = ({
  children,
}) => {
  const auth = useContext(AuthContext);

  const query = useQueryParameters();
  const linkId = query.get('id');
  const { charity } = useCharityData();
  const { data } = useDonationLinkQuery(
    createGraphQlClient(auth),
    {
      input: { donationLinkId: linkId ?? '' },
    },
    {
      enabled: Boolean(linkId),
    },
  );
  const suggestedAmounts = data?.donationLink?.parameters.suggestedAmounts;

  let suggestedAmountDefault: number | null = null;
  const suggestedAmountIndex =
    suggestedAmounts?.findIndex(
      (sa) => sa.amount === data?.donationLink?.parameters.defaultAmount,
    ) ?? -1;
  if (suggestedAmountIndex !== -1) {
    suggestedAmountDefault = suggestedAmountIndex + 1;
  }

  const formMethods = useForm<AllFormData>({
    mode: 'onTouched',
    shouldUnregister: false,
    defaultValues: {
      linkId: data?.donationLink?.donationLinkId,
      donationFrequency: data?.donationLink?.parameters.recurring
        ? 'monthly'
        : 'oneOff',

      numberOfCustomAmounts: suggestedAmounts?.length
        ? suggestedAmounts?.length.toString()
        : suggestedAmountDefaults.numberOfCustomAmounts,
      suggestedAmounts: {
        amount1:
          suggestedAmounts?.[0]?.amount ??
          suggestedAmountDefaults.suggestedAmounts.amount1,
        amount2:
          suggestedAmounts?.[1]?.amount ??
          suggestedAmountDefaults.suggestedAmounts.amount2,
        amount3:
          suggestedAmounts?.[2]?.amount ??
          suggestedAmountDefaults.suggestedAmounts.amount3,
        amount4:
          suggestedAmounts?.[3]?.amount ??
          suggestedAmountDefaults.suggestedAmounts.amount4,
        amount5:
          suggestedAmounts?.[4]?.amount ??
          suggestedAmountDefaults.suggestedAmounts.amount5,
      },
      suggestedAmountDefault:
        suggestedAmountDefault?.toString() ??
        suggestedAmountDefaults.suggestedAmountDefault,

      customAmountsEnabled: suggestedAmounts?.length ? 'yes' : 'no',
      customAmountDescriptionsEnabled: Boolean(
        data?.donationLink?.parameters.suggestedAmounts?.[0]?.description,
      )
        ? 'yes'
        : 'no',
      amountDescriptions: {
        amount1:
          data?.donationLink?.parameters.suggestedAmounts?.[0]?.description ??
          undefined,
        amount2:
          data?.donationLink?.parameters.suggestedAmounts?.[1]?.description ??
          undefined,
        amount3:
          data?.donationLink?.parameters.suggestedAmounts?.[2]?.description ??
          undefined,
        amount4:
          data?.donationLink?.parameters.suggestedAmounts?.[3]?.description ??
          undefined,
        amount5:
          data?.donationLink?.parameters.suggestedAmounts?.[4]?.description ??
          undefined,
      },

      linkTitle: data?.donationLink?.name ?? undefined,
      landingPage: data?.donationLink?.parameters.exitUrl ?? undefined,
      reference: data?.donationLink?.parameters.reference ?? undefined,
      buttonColor: charity?.themeColour?.toLowerCase() ?? '#7a04dd',
      buttonSize: 'small',
      includePaymentLogos: true,
      buttonType: 'justgiving',
      qrCodeType: 'standard',
      qrCodeBackgroundColor: '#ffffff',
      qrCodeForegroundColor: '#000000',
      qrCodeEyeColor: charity?.themeColour?.toLowerCase() ?? '#7a04dd',
      qrCodeIncludeJgLogo: false,
    },
  });

  return <FormProvider {...formMethods}>{children}</FormProvider>;
};
